import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, AlertTriangle, CheckCircle, Loader, Upload, Download, MessageSquarePlus, ArrowRight } from 'lucide-react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { FileUpload } from '../../components/upload/FileUpload';
import { PDFPreview } from '../../components/pdf-viewer/PDFPreview';
import { QuestionCard } from '../../components/question-card/QuestionCard';
import { Carousel } from '../../components/ui/Carousel';
import { Select } from '../../components/ui/Select';
import { DeepDiveModal } from '../../components/shared/DeepDiveModal';
import { SectionModal } from '../../components/shared/SectionModal';
import { AnalysisProgress } from '../../components/shared/AnalysisProgress';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { BOND_QUESTIONS, BOND_QUESTION_SECTIONS } from '../../utils/questions';
import { UserQASection } from '../../components/shared/UserQASection';
import { CollapsibleSection } from '../../components/shared/CollapsibleSection';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const BondDashboard = () => {
    // State
    const [file, setFile] = useState(null);
    const [fileURL, setFileURL] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [translatedAnalysis, setTranslatedAnalysis] = useState(null);
    const [extractedText, setExtractedText] = useState(null);
    const [error, setError] = useState(null);
    const [deepAnalysisCache, setDeepAnalysisCache] = useState({});
    const [selectedLanguage, setSelectedLanguage] = useState('English');
    const [isTranslating, setIsTranslating] = useState(false);
    
    // Streaming state
    const [questionStatuses, setQuestionStatuses] = useState({});
    const [currentProcessingQuestion, setCurrentProcessingQuestion] = useState(null);
    const [totalQuestions, setTotalQuestions] = useState(BOND_QUESTIONS.length);

    // Additional Questions State
    const [additionalQuestions, setAdditionalQuestions] = useState([]);
    const [isGeneratingAdditional, setIsGeneratingAdditional] = useState(false);
    const [hasRequestedAdditional, setHasRequestedAdditional] = useState(false);

    // Modals State
    const [activeQuestion, setActiveQuestion] = useState(null);
    const [activeSection, setActiveSection] = useState(null);

    // Cleanup Object URL
    useEffect(() => {
        return () => {
            if (fileURL) URL.revokeObjectURL(fileURL);
        };
    }, [fileURL]);

    // Handlers
    const handleFileUpload = useCallback((uploadedFile) => {
        setFile(uploadedFile);
        setFileURL(URL.createObjectURL(uploadedFile));
        setError(null);
        setAnalysis(null);
        setTranslatedAnalysis(null);
        setAdditionalQuestions([]);
        setHasRequestedAdditional(false);
        setSelectedLanguage('English');
    }, []);

    const handleClearFile = useCallback(() => {
        setFile(null);
        setFileURL(null);
        setAnalysis(null);
        setTranslatedAnalysis(null);
        setExtractedText(null);
        setError(null);
        setDeepAnalysisCache({});
        setAdditionalQuestions([]);
        setHasRequestedAdditional(false);
        setSelectedLanguage('English');
        setQuestionStatuses({});
        setCurrentProcessingQuestion(null);
    }, []);

    const analyzeContract = async () => {
        if (!file) return;

        setIsLoading(true);
        setError(null);
        setAnalysis({});
        setQuestionStatuses({});
        setCurrentProcessingQuestion(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            // Use streaming endpoint with EventSource
            const response = await fetch(`${API_BASE_URL}/stream-upload-bond-contract?provider=gemini`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to start analysis');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            // Initialize all questions as pending
            const initialStatuses = {};
            BOND_QUESTIONS.forEach((_, idx) => {
                initialStatuses[`Question${idx + 1}`] = 'pending';
            });
            setQuestionStatuses(initialStatuses);

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.trim()) continue;

                    const eventMatch = line.match(/^event: (.+)\ndata: (.+)$/s);
                    if (!eventMatch) continue;

                    const [, eventType, dataStr] = eventMatch;
                    const data = JSON.parse(dataStr);

                    switch (eventType) {
                        case 'ocr_start':
                            console.log('OCR extraction started');
                            break;

                        case 'ocr_complete':
                            console.log('OCR complete:', data);
                            setExtractedText(data.extracted_text);
                            setTotalQuestions(data.total_questions);
                            break;

                        case 'question_start':
                            console.log('Processing question:', data.question_number);
                            setCurrentProcessingQuestion(data.question_number);
                            setQuestionStatuses(prev => ({
                                ...prev,
                                [data.key]: 'processing'
                            }));
                            break;

                        case 'question_complete':
                            console.log('Question complete:', data.question_number);
                            setAnalysis(prev => ({
                                ...prev,
                                [data.key]: [data.answer, data.section]
                            }));
                            setQuestionStatuses(prev => ({
                                ...prev,
                                [data.key]: 'completed'
                            }));
                            setCurrentProcessingQuestion(null);
                            break;

                        case 'complete':
                            console.log('Analysis complete:', data);
                            setIsLoading(false);
                            break;

                        case 'error':
                            console.error('Streaming error:', data);
                            setError(data.error || 'An error occurred during analysis');
                            setIsLoading(false);
                            break;
                    }
                }
            }
        } catch (err) {
            console.error('Analysis error:', err);
            setError(err.message || 'Failed to analyze contract. Please try again.');
            setIsLoading(false);
        }
    };

    const handleLanguageChange = async (language) => {
        if (language === selectedLanguage) return;
        setSelectedLanguage(language);

        if (language === 'English') {
            setTranslatedAnalysis(null);
            return;
        }

        if (!analysis) return;

        setIsTranslating(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/translate-analysis`, {
                analysis_data: analysis,
                target_language: language,
                questions: BOND_QUESTIONS,
                additional_questions: additionalQuestions.length > 0 ? additionalQuestions : undefined
            });
            setTranslatedAnalysis(response.data);
        } catch (err) {
            console.error('Translation error:', err);
            // Fallback to English if translation fails
        } finally {
            setIsTranslating(false);
        }
    };

    const generateAdditionalQuestions = async () => {
        if (!file) return;

        setIsGeneratingAdditional(true);
        try {
            // Simulate additional questions generation
            setHasRequestedAdditional(true);
            setTimeout(() => {
                setAdditionalQuestions([
                    { question: "What are the covenants?", answer: "The bond indenture includes affirmative and negative covenants..." },
                    { question: "Is there a sinking fund provision?", answer: "Yes, Section 5.1 details the sinking fund requirements..." },
                    { question: "What are the events of default?", answer: "Events of default include non-payment of interest, bankruptcy..." }
                ]);
                setIsGeneratingAdditional(false);
            }, 2000);
        } catch (err) {
            console.error('Error generating additional questions:', err);
            setIsGeneratingAdditional(false);
        }
    };

    const handleQuestionClick = (questionKey) => {
        const questionIndex = parseInt(questionKey.replace('Question', ''), 10) - 1;

        // Use translated data if available
        const currentAnalysis = translatedAnalysis || analysis;
        const questionsList = translatedAnalysis?.translated_questions || BOND_QUESTIONS;

        const answerData = currentAnalysis[questionKey];
        const answerText = Array.isArray(answerData) ? answerData[0] : answerData;
        const reference = Array.isArray(answerData) && answerData[1] ? answerData[1] : null;

        setActiveQuestion({
            key: questionKey,
            question: questionsList[questionIndex],
            answer: answerText,
            reference: reference,
        });
    };

    const handleSectionClick = (reference) => {
        setActiveSection({
            reference,
            content: `Content for section ${reference} would appear here if available in the analysis metadata.`
        });
    };

    const currentAnalysis = translatedAnalysis || analysis;
    const currentQuestions = translatedAnalysis?.translated_questions || BOND_QUESTIONS;

    const questionKeys = currentAnalysis
        ? Object.keys(currentAnalysis)
            .filter(key => key.startsWith('Question'))
            .sort((a, b) => {
                const numA = parseInt(a.replace('Question', ''), 10);
                const numB = parseInt(b.replace('Question', ''), 10);
                return numA - numB;
            })
        : [];

    const languageOptions = [
        { value: 'English', label: 'English' },
        { value: 'German', label: 'German' },
        { value: 'French', label: 'French' },
        { value: 'Spanish', label: 'Spanish' },
    ];

    return (
        <DashboardLayout title="Bond Contract Analysis">
            <div className="max-w-7xl mx-auto">
                {/* Progress Indicator - Full width above grid */}
                {isLoading && (
                    <div className="mb-4">
                        <AnalysisProgress fileName={file?.name} contractType="bond" />
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left Column: Upload & Questions */}
                    <div className="space-y-6">
                        {/* Upload Card */}
                        <Card className="p-6">
                            <h2 className="text-xl font-semibold text-slate-900 mb-4">Upload Bond Indenture</h2>
                            <FileUpload
                                onFileUpload={handleFileUpload}
                                isUploading={isLoading}
                                currentFile={file}
                                onClearFile={handleClearFile}
                            />

                            {/* Action Buttons */}
                            <div className="flex gap-3 mt-6">
                                <Button
                                    onClick={analyzeContract}
                                    disabled={!file || isLoading || analysis}
                                    className="flex-1"
                                    isLoading={isLoading}
                                >
                                    {analysis ? 'Analysis Complete' : 'Analyze Contract'}
                                </Button>

                                {(file || analysis) && (
                                    <Button variant="secondary" onClick={handleClearFile} disabled={isLoading}>
                                        Clear
                                    </Button>
                                )}
                            </div>

                            {error && (
                                <div className="mt-4 p-4 bg-danger-50 text-danger-700 rounded-lg flex items-start gap-2">
                                    <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm">{error}</p>
                                </div>
                            )}
                        </Card>

                        {/* Analysis Results */}
                        {(analysis || isLoading) && (
                            <div className="space-y-2 animate-slide-up">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-semibold text-slate-900">Analysis Results</h2>
                                    <div className="flex items-center gap-3">
                                        {!isLoading && (
                                            <div className="w-32">
                                                <Select
                                                    value={selectedLanguage}
                                                    onChange={handleLanguageChange}
                                                    options={languageOptions}
                                                />
                                            </div>
                                        )}
                                        <div className="text-sm text-slate-500">
                                            {Object.keys(questionStatuses).filter(k => questionStatuses[k] === 'completed').length} / {totalQuestions} Completed
                                        </div>
                                    </div>
                                </div>

                                {isTranslating ? (
                                    <div className="flex items-center justify-center py-12">
                                        <Loader className="h-8 w-8 text-primary-500 animate-spin" />
                                        <span className="ml-3 text-slate-600">Translating analysis...</span>
                                    </div>
                                ) : (
                                    <div className="w-full space-y-3">
                                        {BOND_QUESTION_SECTIONS.map((section, sectionIdx) => (
                                            <CollapsibleSection
                                                key={section.title}
                                                title={section.title}
                                                index={sectionIdx}
                                                questionStatuses={questionStatuses}
                                                startIdx={section.start}
                                                endIdx={section.end}
                                            >
                                                {BOND_QUESTIONS.slice(section.start, section.end).map((q, i) => {
                                                    const idx = section.start + i;
                                                    const key = `Question${idx + 1}`;
                                                    const answerData = currentAnalysis[key];
                                                    const answerText = Array.isArray(answerData) ? answerData[0] : answerData;
                                                    const reference = Array.isArray(answerData) && answerData[1] ? answerData[1] : null;
                                                    const status = questionStatuses[key] || 'pending';

                                                    return (
                                                        <QuestionCard
                                                            key={key}
                                                            number={idx + 1}
                                                            question={currentQuestions[idx]}
                                                            answer={answerText}
                                                            reference={reference}
                                                            onClick={() => handleQuestionClick(key)}
                                                            status={status}
                                                        />
                                                    );
                                                })}
                                            </CollapsibleSection>
                                        ))}
                                    </div>
                                )}

                                {/* AI Q&A Notification */}
                                {!isLoading && Object.keys(questionStatuses).filter(k => questionStatuses[k] === 'completed').length === totalQuestions && (
                                    <div
                                        onClick={() => {
                                            const el = document.getElementById('user-qa-input');
                                            if (el) {
                                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                setTimeout(() => {
                                                    const input = el.querySelector('input[type="text"]');
                                                    if (input) input.focus();
                                                }, 500);
                                            }
                                        }}
                                        className="mt-6 p-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl border border-primary-200 flex items-center gap-4 animate-fade-in cursor-pointer hover:shadow-md hover:border-primary-300 transition-all duration-200">
                                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100 shrink-0">
                                            <MessageSquarePlus className="h-5 w-5 text-primary-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-primary-900">Analysis complete! Have more questions?</p>
                                            <p className="text-xs text-primary-700 mt-0.5">You can now ask the AI anything about this document using the Q&A panel on the right.</p>
                                        </div>
                                        <ArrowRight className="h-5 w-5 text-primary-400 shrink-0 hidden sm:block animate-pulse" />
                                    </div>
                                )}

                                {/* Additional Questions Section - Only show when all questions are completed */}
                                {!isLoading && Object.keys(questionStatuses).filter(k => questionStatuses[k] === 'completed').length === totalQuestions && (
                                    !hasRequestedAdditional ? (
                                        <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200 text-center">
                                            <h3 className="text-lg font-medium text-slate-900 mb-2">Need more insights?</h3>
                                            <p className="text-slate-600 mb-4">Generate additional questions to explore the contract further.</p>
                                            <Button
                                                variant="outline"
                                                onClick={generateAdditionalQuestions}
                                                isLoading={isGeneratingAdditional}
                                            >
                                                Explore More Questions
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="space-y-4 mt-12 animate-fade-in">
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-lg font-semibold text-slate-900">Additional Insights</h3>
                                                <span className="text-sm text-slate-500">{additionalQuestions.length} new questions</span>
                                            </div>
                                            <div className="space-y-4">
                                                {additionalQuestions.map((item, index) => (
                                                    <QuestionCard
                                                        key={`additional-${index}`}
                                                        number={questionKeys.length + index + 1}
                                                        question={item.question}
                                                        answer={item.answer}
                                                        onClick={() => setActiveQuestion({
                                                            key: `additional-${index}`,
                                                            question: item.question,
                                                            answer: item.answer
                                                        })}
                                                        status="success"
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        )}
                    </div>

                    {/* Right Column: PDF Preview + Q&A */}
                    <div className="relative space-y-6">
                        <PDFPreview fileURL={fileURL} />
                        {!isLoading && analysis && (
                            <UserQASection
                                extractedText={extractedText}
                                fileName={file?.name}
                                analysisComplete={!isLoading && !!analysis}
                                apiBaseUrl={API_BASE_URL}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Modals */}
            <DeepDiveModal
                isOpen={!!activeQuestion}
                onClose={() => setActiveQuestion(null)}
                question={activeQuestion?.question}
                answer={activeQuestion?.answer}
                reference={activeQuestion?.reference}
                fileName={file?.name}
                extractedText={extractedText}
                deepAnalysisCache={deepAnalysisCache}
                setDeepAnalysisCache={setDeepAnalysisCache}
                analysisData={currentAnalysis}
                selectedLanguage={selectedLanguage}
                handleSectionClick={handleSectionClick}
                analysisMode="bond"
            />

            <SectionModal
                isOpen={!!activeSection}
                onClose={() => setActiveSection(null)}
                sectionReference={activeSection?.reference}
                sectionContent={activeSection?.content}
                fileName={file?.name}
                extractedText={extractedText}
            />
        </DashboardLayout>
    );
};